import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Select, Space, Typography } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import {
    useDataProvider,
    useNotify,
    usePermissions,
    useQuery,
    useQueryWithStore,
    useRedirect,
} from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import { Field, FormSection, NetorcaForm, PromptTextArea, useServerErrors } from '../Common/Form';
import { EntityFormRoute, ServiceField } from '../Common/Integrations';
import MainCard from '../Common/MainCard/MainCard';
import DeleteButton from '../Common/DeleteButton';
import { SchemaControl } from './SchemaField';
import PackForm from './AIProcessorExtraDataForms/PackForm';
import OptimiserForm from './AIProcessorExtraDataForms/OptimiserForm';
import ValidatorForm from './AIProcessorExtraDataForms/ValidatorForm';
import { EvaluationCase } from './Evaluation/types';
import { CaseErrors, NO_CASE_ERRORS, readCaseErrors, withoutCaseErrors } from './Evaluation/utils';
import './AIProcessorForm.css';

const RESOURCE = 'serviceowner_ai_processors';
const TITLE = 'AI Processor';

/** every stage a processor can run at */
const ACTION_TYPES = [
    { value: 'config', label: 'Config' },
    { value: 'verify', label: 'Verify' },
    { value: 'execution', label: 'Execution' },
    { value: 'optimiser', label: 'Optimiser' },
    { value: 'change_instance_validator', label: 'Change Instance Validator' },
];

/** the three that run over a pack pipeline — the other two are their own thing */
const PIPELINE_TYPES = ['config', 'verify', 'execution'];

/** AIProcessorSerializer.Meta.fields — anything else the API rejects is a general error */
const AI_PROCESSOR_FIELDS = [
    'name',
    'service',
    'llm_model',
    'action_type',
    'prompt',
    'response_schema',
    'extra_data',
];

/** the one action type that carries evaluation cases; the API 400s on any other */
const VALIDATOR = 'change_instance_validator';

const EXTRA_DATA_DEFAULTS = {
    include_change_instance: false,
    enable_pack_context: false,
    include_previous_declaration: false,
    include_service_config: false,
    enable_generative_ui: false,
    allow_auto_approval: false,
    allow_auto_rejection: false,
    send_service_info: false,
    send_existing_service_items: false,
    evaluation: [] as EvaluationCase[],
};

/** a JSON field is edited as text, whichever way the API sent it */
const asText = (value: any) => {
    if (value === null || value === undefined || value === '') return null;
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
};

/** and goes back as JSON the moment it parses as JSON */
const asJson = (value: any) => {
    if (typeof value !== 'string') return value ?? null;
    if (!value.trim()) return null;
    try {
        return JSON.parse(value);
    } catch (e) {
        return value;
    }
};

/**
 * One reading of the form's shape, used for the record, for a past version and
 * for a blank create — so a version is looked at through the very fields that
 * edit it, and nothing has to be laid out twice.
 */
const toFormValues = (source?: any) => ({
    name: source?.name,
    service: source?.service?.id ?? source?.service,
    llm_model: source?.llm_model?.id ?? source?.llm_model,
    action_type: source?.action_type,
    prompt: source?.prompt,
    response_schema: asText(source?.response_schema),
    extra_data: {
        ...EXTRA_DATA_DEFAULTS,
        ...(source?.extra_data || {}),
        // the evaluation cases are a key of extra_data, not a field of their
        // own — the API reports them as [] rather than null, but a record from
        // anywhere else may not carry the key at all
        evaluation: Array.isArray(source?.extra_data?.evaluation) ? source.extra_data.evaluation : [],
        generative_ui_schema: asText(source?.extra_data?.generative_ui_schema),
    },
});

/** the models this team can pick from; the list is short and the picker needs all of it */
const useLlmModelOptions = () => {
    const { data, loading } = useQueryWithStore({
        type: 'getList',
        resource: 'llm_models',
        payload: { pagination: { page: 1, perPage: 200 }, sort: { field: 'name', order: 'ASC' }, filter: {} },
    });

    return {
        options: ((data as any[]) || []).map((model) => ({ value: model.id, label: model.name })),
        loading,
    };
};

/** with the Universal Executor on, the service writes the response schema itself */
const useUniversalExecutor = (serviceId?: number) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const [enabled, setEnabled] = useState(false);

    useEffect(() => {
        if (!serviceId) {
            setEnabled(false);
            return undefined;
        }
        let cancelled = false;
        dataProvider
            .getAction('serviceowner_pack_profile', { id: undefined, verb: `service/${serviceId}/config` })
            .then(({ data }: any) => {
                if (!cancelled) setEnabled(!!data?.universal_executor_enabled);
            })
            .catch(() => {
                if (!cancelled) setEnabled(false);
            });
        return () => {
            cancelled = true;
        };
    }, [serviceId, dataProvider]);

    return enabled;
};

/**
 * Every earlier version of this processor. Picking one shows it in the form
 * read-only; the latest one is the record itself, so picking that is the same
 * as clearing the picker.
 */
const VersionPicker = ({
    record,
    viewing,
    savedCount,
    onView,
    onAdopt,
}: {
    record: any;
    viewing: any;
    /** every save writes a new version, and the list here has to hear about it */
    savedCount: number;
    onView: (version: any) => void;
    onAdopt: () => void;
}) => {
    const { data, refetch } = useQuery({
        type: 'getAction',
        resource: RESOURCE,
        payload: { id: record.id, verb: 'history', filter: {} },
    });

    /* The page stays put after a save, so this list is the one thing on it that
       would otherwise still be describing the record as it was loaded. */
    useEffect(() => {
        if (savedCount) refetch();
        // refetch is stable; keying on it as well would re-run on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedCount]);

    const versions: any[] = data?.results ?? [];
    if (!versions.length) return null;

    const latestId = String(versions[0].history_id);

    return (
        <Space>
            <Select
                style={{ width: 180 }}
                placeholder="Version history"
                allowClear
                value={viewing ? String(viewing.history_id) : undefined}
                options={versions.map((version) => ({
                    value: String(version.history_id),
                    label: version.version
                        ? `Version ${version.version}`
                        : new Date(version.history_date).toLocaleDateString(),
                }))}
                onChange={(value) => {
                    const picked = versions.find((version) => String(version.history_id) === value);
                    onView(value && value !== latestId ? picked : null);
                }}
            />
            {viewing && (
                <Button type="primary" onClick={onAdopt}>
                    Set as current
                </Button>
            )}
        </Space>
    );
};

const AIProcessorForm = ({ record }: { record?: any }) => {
    const [form] = Form.useForm();
    const { permissions } = usePermissions();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const { apply, fieldErrors, clear } = useServerErrors(form, AI_PROCESSOR_FIELDS);
    const [saving, setSaving] = useState(false);
    /* `extra_data.evaluation` comes back as a list the API indexes by position,
       which no single form field can carry — it is shown against the cases it
       is about instead, and kept out of the generic handler below. */
    const [caseErrors, setCaseErrors] = useState<CaseErrors>(NO_CASE_ERRORS);

    /** an older version being looked at, or null for the record itself */
    const [viewing, setViewing] = useState<any>(null);
    /** an older version copied over the form by "Set as current" */
    const [adopted, setAdopted] = useState<any>(null);
    /**
     * What the last save put on the server.
     *
     * The page stays put after a save — an evaluation is run from here, and
     * being thrown back to the list to walk in again is no way to run one — so
     * the record react-admin loaded is no longer what the server holds. This
     * is, and it is what "unsaved changes" is measured against.
     */
    const [saved, setSaved] = useState<any>(null);
    /** the form is ahead of the server: a run would use what the server still has */
    const [dirty, setDirty] = useState(false);
    /** bumped on every save that lands, which is what puts the form back level */
    const [savedCount, setSavedCount] = useState(0);

    const isAdmin = permissions === 'ADMIN';
    const editing = !!record;
    const readOnly = !isAdmin || !!viewing;

    const { options: llmOptions, loading: llmLoading } = useLlmModelOptions();

    const actionType = Form.useWatch('action_type', form);
    const serviceId = Form.useWatch('service', form);
    const universalExecutor = useUniversalExecutor(serviceId);

    const isPipeline = PIPELINE_TYPES.includes(actionType);
    /** the API refuses a schema of your own while the Universal Executor is on */
    const schemaManaged = universalExecutor && isPipeline;

    // whatever the form is showing — the record, a version, or a version taken on
    const source = viewing ?? adopted ?? saved ?? record;
    /* What the server holds for this processor: the last save if there was
       one, else what was loaded. A run is measured against this, never
       against what the form currently says. */
    const persisted = saved ?? record;
    useEffect(() => {
        form.resetFields();
        if (source) form.setFieldsValue(toFormValues(source));
    }, [source, form]);

    const onFinish = (values: any) => {
        if (viewing) {
            notify('Cannot save while looking at an older version', 'warning');
            return;
        }
        clear();
        setCaseErrors(NO_CASE_ERRORS);
        setSaving(true);

        /* The evaluation section is a card that can be shut, and a shut card
           has no mounted field — antd then leaves `extra_data.evaluation` out
           of the submitted values, though it is still in the store. Reading it
           off the store is what makes saving from a collapsed card mean the
           same as saving from an open one. */
        const evaluation = form.getFieldValue(['extra_data', 'evaluation']);

        /* The API writes `extra_data` as a whole: every key left out of it is
           reset to its default, so a save that only meant to change one flag
           would clear the evaluation cases, and a save of the cases would clear
           every flag. Sending it whole is what stops that — the record's own
           keys underneath, so a setting this form does not show survives too. */
        const extraData: any = {
            ...EXTRA_DATA_DEFAULTS,
            ...(source?.extra_data || {}),
            ...(values.extra_data || {}),
        };
        // edited as text wherever it is shown, stored as JSON
        extraData.generative_ui_schema = asJson(extraData.generative_ui_schema);
        // cases on anything but a validator are a 400, not an empty list
        if (values.action_type !== VALIDATOR) delete extraData.evaluation;
        else extraData.evaluation = Array.isArray(evaluation) ? evaluation : [];

        const payload = {
            ...values,
            service: typeof values.service === 'object' ? values.service?.value : values.service,
            llm_model: typeof values.llm_model === 'object' ? values.llm_model?.value : values.llm_model,
            response_schema: asJson(values.response_schema),
            extra_data: extraData,
        };

        const request = editing
            ? dataProvider.update(RESOURCE, { id: record.id, data: payload, previousData: { id: record.id } })
            : dataProvider.create(RESOURCE, { data: payload });

        request
            .then((response: any) => {
                notify(editing ? `${TITLE} updated` : `${TITLE} created`);
                setSaving(false);
                setDirty(false);
                setSavedCount((previous) => previous + 1);

                /* Staying here is the point: an evaluation runs against what
                   the server holds, so the save that makes a run meaningful
                   should leave the reader in front of the button that starts
                   it. A create has nowhere to stay, so it opens the record it
                   just made rather than the list it was never on. */
                /* What was already known, then what was sent, then whatever
                   the API said back. The form does not edit every field a
                   processor has — `active` is a switch on the list — so
                   building this out of the payload alone would lose them. */
                const persisted = { ...(source || {}), ...payload, ...(response?.data || {}) };
                if (editing) setSaved(persisted);
                else if (persisted.id) redirect(`/${RESOURCE}/${persisted.id}`);
                else redirect(`/${RESOURCE}`);
            })
            .catch((error: any) => {
                setCaseErrors(readCaseErrors(error?.body));
                apply(
                    { status: error?.status, message: error?.message, body: withoutCaseErrors(error?.body) },
                    editing ? `Could not update the ${TITLE}` : `Could not create the ${TITLE}`,
                );
                setSaving(false);
            });
    };

    const options = isPipeline ? (
        <PackForm disabled={readOnly} />
    ) : actionType === 'optimiser' ? (
        <OptimiserForm />
    ) : actionType === VALIDATOR ? (
        <ValidatorForm
            form={form}
            record={persisted}
            historical={!!viewing}
            readOnly={readOnly}
            unsaved={dirty}
            caseErrors={caseErrors}
        />
    ) : null;

    return (
        <NetorcaForm
            className="nf-form--sheet aip-form"
            fill
            form={form}
            name="ai_processor"
            /* no eyebrow: the arrow beside the name already says what list it
               belongs to, and the name is what the page is about */
            title={editing ? record.name || TITLE : 'New AI processor'}
            onBack={() => redirect(`/${RESOURCE}`)}
            backLabel="Back to AI processors"
            readOnly={readOnly}
            /* the picker beside it already names the version being read, and
               every field is visibly out of reach — saying it again is noise */
            readOnlyNotice={viewing ? false : 'You have read-only access to this record'}
            heroExtra={
                editing && (
                    <VersionPicker
                        record={record}
                        viewing={viewing}
                        savedCount={savedCount}
                        onView={setViewing}
                        onAdopt={() => {
                            setAdopted({ ...viewing });
                            setViewing(null);
                        }}
                    />
                )
            }
            submitLabel={editing ? 'Save changes' : 'Create AI processor'}
            submitting={saving}
            onCancel={() => redirect(`/${RESOURCE}`)}
            onFinish={onFinish}
            onValuesChange={() => setDirty(true)}
            /* the form and the server are level again, so the unsaved marker
               and the run button both stand down */
            pristineKey={savedCount}
            serverErrors={fieldErrors}
            destructive={
                editing && isAdmin ? <DeleteButton title={TITLE} resource={RESOURCE} record={record} /> : undefined
            }
            initialValues={toFormValues(record)}
        >
            {/* One grid for the whole form: what the processor is on the first
                row, then the prompt, then everything the chosen type turns on —
                each field taking the next place that fits, so no row is left
                half empty and nothing needs a card of its own. */}
            <FormSection id="processor" title="AI processor" bare>
                <Field name="name" label="Name" span={3} rules={[{ required: true, message: 'Give the processor a name' }]}>
                    <Input placeholder="Firewall config generator" />
                </Field>

                <Field
                    name="action_type"
                    label="Type"
                    span={3}
                    rules={[{ required: true, message: 'Choose what this processor does' }]}
                >
                    <Select placeholder="Select a type" options={ACTION_TYPES} />
                </Field>

                <ServiceField span={3} />

                <Field
                    name="llm_model"
                    label="LLM model"
                    span={3}
                    rules={[{ required: true, message: 'Choose the model to run' }]}
                >
                    <Select
                        placeholder="Select an LLM model"
                        options={llmOptions}
                        loading={llmLoading}
                        showSearch={{ optionFilterProp: 'label' }}
                    />
                </Field>

                {/* A property of the service, said once where the service is
                    chosen — the response schema field further down only says it
                    for the stage in front of you, and only when you get there. */}
                {universalExecutor && (
                    <div className="aip-note" role="status">
                        <ThunderboltOutlined aria-hidden="true" />
                        <span>
                            <strong>Universal Executor is on for this service.</strong> It writes the response
                            schema for the Config, Verify and Execution stages, so a processor for one of those
                            does not set its own.
                        </span>
                    </div>
                )}

                {actionType && <div className="nf-rule" />}

                {actionType && actionType !== 'optimiser' && (
                    <Field
                        className="aip-prompt"
                        name="prompt"
                        label="Prompt"
                        rules={[{ required: true, message: 'Write the prompt the model runs on' }]}
                    >
                        <PromptTextArea
                            maxLength={10000}
                            showCount
                            placeholder="Describe what the model should produce…"
                        />
                    </Field>
                )}

                {isPipeline &&
                    (schemaManaged ? (
                        <Field label="Response schema" span={3}>
                            <Typography.Text type="secondary">
                                Generated by the Universal Executor for this service.
                            </Typography.Text>
                        </Field>
                    ) : (
                        <Field
                            name="response_schema"
                            label="Response schema"
                            span={3}
                            hint="Optional JSON schema the model's answer has to conform to."
                        >
                            <SchemaControl label="response schema" disabled={readOnly} />
                        </Field>
                    ))}

                {options}
            </FormSection>
        </NetorcaForm>
    );
};

const AIProcessorCreate = (props: any) => (
    <EntityFormRoute
        id={props.id}
        resource={RESOURCE}
        basePath={`/${RESOURCE}`}
        render={(record) => (
            /* not `aip-card`: that is the evaluation card's class, and its
               `display: block` and `overflow: hidden` landed on the frame —
               the form could not fill it, nor its action bar stick */
            <MainCard className="aip-page">
                <AIProcessorForm record={record} />
            </MainCard>
        )}
    />
);

export default AIProcessorCreate;
